import { Component, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { OtpService } from '@app/services/authenticators/otp.service';
import { CertificateService } from '@app/services/certificates/certificate.service';
import { GlobalState } from '@app/store/store';
import { Button } from '@infocert/ng-ui-kit/models/button';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom, tap } from 'rxjs';

@Component({
  selector: 'app-manage-cert',
  templateUrl: './manage-cert.component.html',
  styleUrls: ['./manage-cert.component.scss'],
  standalone: false,
})
@UntilDestroy()
export class ManageCertComponent implements OnInit {
  constructor(
    private store: Store<GlobalState>,
    private otpService: OtpService,
    private certificateService: CertificateService,
    private translateService: TranslateService
  ) {}

  loading: boolean = false;
  showStep1: any = false;
  showStep2: any = false;
  step1Attempted = false;
  step2Attempted = false;
  showSuccess: boolean = false;
  automatic: boolean = false;
  isPkBox: boolean = false;

  active: boolean = false;

  get operation(): string {
    return this.active ? 'DEACTIVATE' : 'ACTIVATE';
  }
  get iconText(): string {
    return this.active ? 'COMMON.ACTIVE' : 'COMMON.NEEDS_ACTIVATE';
  }
  get descText(): string {
    return this.active ? 'COMMON.TO_DISACTIVATE' : 'COMMON.TO_ACTIVATE';
  }

  get buttonText(): string {
    return this.active ? 'COMMON.DISACTIVATE' : 'COMMON.ACTIVATE';
  }

  get successText(): string {
    return this.active ? 'COMMON.ACTIVATED' : 'COMMON.DISACTIVATED';
  }

  get cardFAButton(): Button {
    const label = this.translateService.instant(
      'SUSPEND_PAGE.AUTOMATIC_CARD.BUTTON_TEXT',
      {
        state: this.translateService.instant(
          this.active ? 'COMMON.DISACTIVATE' : 'COMMON.ACTIVATE'
        ),
      }
    );
    return {
      label,
      altText: label,
      color: 'text',
      onClick: () => {
        this.openModal('MANAGE_CERT');
        this.showStep1 = true;
      },
    };
  }

  ngOnInit(): void {
    // Dopo un tentativo di submit, nasconde i validator mentre l'utente digita.
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.step1Attempted) {
        this.step1Attempted = false;
      }
      if (this.step2Attempted) {
        this.step2Attempted = false;
      }
    });

    this.store
      .pipe(
        untilDestroyed(this),
        tap(store => {
          if (store.session) {
            this.isPkBox = !store.session.icssUser && !store.session?.trissUser;
            this.automatic = store.session.automatic;
          }

          if (store.certificate) {
            this.active = store.certificate.active;
          }
        })
      )
      .subscribe();
  }

  pin = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(8),
    Validators.maxLength(8),
  ]);

  otp = new UntypedFormControl('', [
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);

  form = new UntypedFormGroup({
    pin: this.pin,
    otp: this.otp,
  });

  // convenience getter for easy access to form fields
  get f() {
    return this.form.controls;
  }

  get formErrors() {
    const errors = this.form.errors;
    return errors;
  }

  private getOtpDisclaimerText(): string {
    const rawValue = (this.otp?.value ?? '').toString().trim();
    if (!rawValue || rawValue === 'null') {
      return this.translateService.instant('COMMON.VALIDATORS.REQUIRED');
    }

    const errors = this.otp?.errors;
    if (!errors) return '';

    if (errors['minlength']) {
      return this.translateService.instant('COMMON.VALIDATORS.MIN_LENGTH');
    }
    if (errors['maxlength']) {
      return this.translateService.instant('COMMON.VALIDATORS.MAX_LENGTH');
    }
    if (errors['required']) {
      return this.translateService.instant('COMMON.VALIDATORS.REQUIRED');
    }
    return '';
  }

  getManageCertOtpErrorDisclaimerText(): string {
    if (!this.step2Attempted || this.otp.valid) return '';
    return this.getOtpDisclaimerText();
  }

  onSubmit() {
    this.loading = true;
    this.ensureOtpStep2Validators();
    // stop here if form is invalid
    if (this.form.invalid) {
      this.step2Attempted = true;
      this.form.markAllAsTouched();
      this.loading = false;
      return;
    }

    this.certificateService
      //@ts-ignore
      .manageCertificate(
        this.f['pin'].value.trim(),
        this.operation,
        this.f['otp'].value.trim()
      )
      .subscribe({
        next: async response => {
          console.log('🚀 ~ PinPageComponent ~ onSubmit ~ response', response);

          this.loading = false;

          // Se tutto va bene

          this.clearForm();

          const certificate = await lastValueFrom(
            this.certificateService.getCertificate()
          );

          this.active = certificate.active;

          this.showStep1 = false;
          this.showStep2 = false;
          this.closeModal();
          this.showSuccess = true;
        },
        error: error => {
          console.error(error);
          this.goToStepOne();
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  goToSecondStep() {
    if (this.form.invalid) {
      this.step1Attempted = true;
      this.form.markAllAsTouched();
      return;
    }
    this.step1Attempted = false;
    this.sendOtp();
    this.ensureOtpStep2Validators();

    this.showStep1 = false;
    this.showStep2 = true;
  }

  goToStepOne() {
    this.step2Attempted = false;
    this.f['otp'].setValue('');
    this.otpService.resetOtpInputVal(true);
    this.clearForm();
    this.showStep1 = true;
    this.showStep2 = false;
  }

  clearForm() {
    this.step1Attempted = false;
    this.step2Attempted = false;

    this.pin.setValidators([
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(8),
    ]);
    this.pin.setValue('', { emitEvent: true });
    this.pin.updateValueAndValidity({ emitEvent: false });

    this.otp.clearValidators();
    this.otp.setValidators([Validators.minLength(6), Validators.maxLength(8)]);
    this.otp.setValue('', { emitEvent: true });
    this.otp.updateValueAndValidity({ emitEvent: false });
  }

  private ensureOtpStep2Validators(): void {
    this.otp.setValidators([
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(8),
    ]);
    this.otp.updateValueAndValidity({ emitEvent: false });
  }

  sendOtp() {
    this.otpService.generateCode().subscribe({
      next: async response => {
        // Se tutto va bene
      },
      error: error => {
        console.error(error);
      },
      complete: () => {},
    });
  }

  public manageCertModalVisible = false;

  openModal(name: string = '') {
    this.manageCertModalVisible = true;
  }

  onToggleManageCertModal(visible: boolean): void {
    this.manageCertModalVisible = visible;
    if (!visible) {
      this.closeModal();
    }
  }

  closeModal() {
    this.manageCertModalVisible = false;
  }
}
